import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';

// Backs every FieldHelp "?" bubble's admin-editable content (layer 3 of the
// in-app help stack; see HelpSheetContext for layer 4's sibling). Unlike
// Screen Help, there's no central manifest of every FieldHelp call site
// (ALL_NAV_ITEMS/EXTRA_SCREENS covers every *screen*, but not every field
// help bubble on it) — so instead of one batched sync on boot, each
// FieldHelp registers its own default the first time it mounts, and this
// context just tracks "have we already sent that registration this
// session" so a re-render doesn't re-POST it.
const FieldHelpRegistryContext = createContext(null);

export function FieldHelpRegistryProvider({ children }) {
  const [entries, setEntries] = useState({});
  const syncedKeys = useRef(new Set());

  const reload = useCallback(async () => {
    try {
      const res = await axios.get('/api/field-help', { params: { all: true } });
      const byKey = {};
      (res.data?.data || []).forEach((row) => { byKey[row.field_key] = row; });
      setEntries(byKey);
    } catch (err) {
      // A field help bubble just falls back to its own hardcoded default
      // text/article if the registry never loads — see FieldHelp.js.
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Fire-and-forget: additive/idempotent server-side (see
  // fieldHelpController.syncEntry), so calling it more than once for the
  // same field_key is harmless — the ref just keeps this to once per key
  // per page load rather than once per every mount/remount.
  const registerDefault = useCallback((entry) => {
    if (!entry?.field_key || syncedKeys.current.has(entry.field_key)) return;
    syncedKeys.current.add(entry.field_key);
    axios.post('/api/field-help/sync', entry).then(reload).catch(() => {
      syncedKeys.current.delete(entry.field_key);
    });
  }, [reload]);

  return (
    <FieldHelpRegistryContext.Provider value={{ entries, registerDefault }}>
      {children}
    </FieldHelpRegistryContext.Provider>
  );
}

export function useFieldHelpRegistry() {
  return useContext(FieldHelpRegistryContext);
}

export default FieldHelpRegistryContext;
